const DB_NAME = 'c64-vault-local-media'
const DB_VERSION = 1
const STORE_NAME = 'media'

export interface LocalMediaRecord {
  id: string
  name: string
  size: number
  type: string
  sha256: string
  addedAt: number
  blob: Blob
}

export const supportedExtensions = ['prg', 'd64', 'crt', 't64', 'tap'] as const

export function getExtension(name: string): string {
  const part = name.toLowerCase().split('.').pop()
  return part ?? ''
}

export function isSupportedMedia(name: string): boolean {
  return supportedExtensions.includes(getExtension(name) as (typeof supportedExtensions)[number])
}

export async function sha256(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
  })
}

export async function saveLocalMedia(file: File, digest: string): Promise<LocalMediaRecord> {
  const db = await openDatabase()
  const record: LocalMediaRecord = {
    id: digest,
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    sha256: digest,
    addedAt: Date.now(),
    blob: file,
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(record)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
  return record
}

export async function listLocalMedia(): Promise<LocalMediaRecord[]> {
  const db = await openDatabase()
  const rows = await new Promise<LocalMediaRecord[]>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  db.close()
  return rows.sort((a, b) => b.addedAt - a.addedAt)
}

export async function deleteLocalMedia(id: string): Promise<void> {
  const db = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}
